// CustomForm.tsx
import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  TextField,
  Autocomplete,
  Paper,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import { Description, Delete, Download } from "@mui/icons-material";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  Applicant,
  ApplicationData,
  ApprovalActivity,
  Approver,
  FormData,
  Group,
  Profile,
  Referent,
  ReferentUser,
} from "../RecordDetail/ApplicationData";
import FormLinkList from "./FormLinkList";
import ApplicationNumberFields from "./ApplicationNumberFields";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Link from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extensions';
import { EditorView } from "@tiptap/pm/view";
import { RichTextToolbar, FontSize, Indent } from './RichTextToolbar';

// ────────── リッチテキストフィールドコンポーネント ──────────

type RichTextFieldProps = {
  control: any;
  setValue: any;
  name: string;
  label: string;
};

const RichTextField = ({ control, setValue, name, label }: RichTextFieldProps) => {
  const value = useWatch({ control, name }) as string | undefined;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontSize,
      Indent,
      Link.configure({ openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: label }),
      Image.configure({ allowBase64: true }),
    ],
    content: value ?? "",
    editorProps: {
      handleDOMEvents: {
        // 画像ファイルのドラッグ＆ドロップ対応
        drop: (view: EditorView, event: DragEvent) => {
          const files = event.dataTransfer?.files;
          if (!files || files.length === 0) return false;

          Array.from(files).forEach((file) => {
            if (!file.type.startsWith("image/")) return;
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = reader.result as string;
              editor?.chain().focus().setImage({ src: base64 }).run();
            };
            reader.readAsDataURL(file);
          });

          event.preventDefault();
          return true;
        },
      },
      attributes: {
        class: "prose-editor",
      },
    },
    onUpdate: ({ editor }: { editor: any }) => {
      setValue(name, editor.getHTML(), { shouldDirty: true });
    },
  });

  // 外部からvalueが変わった場合の同期（一時保存復元など）
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value ?? "";
    if (current !== next) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <Box sx={{ width: "100%" }}>
      <FormControl fullWidth>
        {/* ツールバー */}
        <RichTextToolbar editor={editor} />

        {/* エディタ枠 */}
        <Box
          sx={{
            border: 1,
            borderColor: "rgba(0, 0, 0, 0.23)",
            borderTop: 0,
            borderRadius: "0 0 4px 4px",
            p: 1,
            minHeight: 240,
            "&:focus-within": { borderColor: "primary.main" },
            // プレースホルダー表示用CSS
            ".prose-editor p.is-empty:first-child::before": {
              content: "attr(data-placeholder)",
              color: "#9e9e9e",
              float: "left",
              height: 0,
              pointerEvents: "none",
            },
            ".prose-editor p.is-empty": { margin: 0 },
            // エディタ基本スタイル
            ".prose-editor": {
              minHeight: 240,
              outline: "none",
              lineHeight: 1.6,
            },
            // テーブルスタイル
            "& table": {
              borderCollapse: "collapse",
              width: "100%",
              margin: "0.5em 0",
            },
            "& th, & td": {
              border: "1px solid #dee2e6",
              padding: "0.5em",
              minWidth: "50px",
              position: "relative",
            },
            "& th": {
              backgroundColor: "#f8f9fa",
              fontWeight: "bold",
            },
            "& .selectedCell::after": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              background: "rgba(200, 200, 255, 0.4)",
              pointerEvents: "none",
              zIndex: 2,
            },
            // 引用スタイル
            "& blockquote": {
              borderLeft: "3px solid #ccc",
              paddingLeft: "1em",
              margin: "0.5em 0",
              color: "#666",
            },
            // コードブロックスタイル
            "& pre": {
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              padding: "0.75em 1em",
              overflowX: "auto",
              fontFamily: "monospace",
            },
            // インラインコードスタイル
            "& code": {
              backgroundColor: "#f0f0f0",
              borderRadius: "3px",
              padding: "0.1em 0.3em",
              fontFamily: "monospace",
              fontSize: "0.9em",
            },
            // 水平線スタイル
            "& hr": {
              border: "none",
              borderTop: "2px solid #dee2e6",
              margin: "1em 0",
            },
            // 画像スタイル
            "& img": {
              maxWidth: "100%",
              height: "auto",
            },
          }}
        >
          <Controller
            name={name}
            control={control}
            render={() => (
              <EditorContent editor={editor} className="prose-editor" />
            )}
          />
        </Box>
      </FormControl>
    </Box>
  );
};

// ────────── カスタムフォーム ──────────

export type CustomFormProps = {
  onDrop: (acceptedFiles: File[], index: number) => void;
  onTempSave: (e: React.FormEvent) => void;
};

type FormDataSection = {
  sectionName?: string;
  formDataList?: FormDataRender[];
};

type FormDataRender = FormData & {
  index: number;
};

type ValidationMessage = { isVisible: boolean; message?: string };

type SelectReferentOption = {
  label: string;
  type: string;
};

export const CustomForm = ({ onDrop, onTempSave }: CustomFormProps) => {
  const navigate = useNavigate();
  const {
    getValues,
    setValue,
    register,
    control,
    formState: { errors },
  } = useFormContext<ApplicationData>();
  const applicantList = useWatch({ control, name: "applicantList" }) ?? [];
  const formDataField = useFieldArray({ control, name: "formEntryList" });
  console.log("formDataField.fields", formDataField.fields);

  const approverActivityField = useFieldArray({
    control,
    name: "approvalActivityList",
  });
  const selectReferent: Referent =
    useWatch({ control, name: "selectReferent" }) ?? {};
  const [hoveredFileIndex, setHoveredFileIndex] = useState<number | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const selectReferentOptions: SelectReferentOption[] = useMemo(
    () => [
      ...(selectReferent.userList ?? []).map((user) => ({
        label: user.lastName + user.firstName,
        type: "user",
      })),
      ...(selectReferent.profileList ?? [])
        .map((profile) => ({
          label: profile.value,
          type: "profile",
        }))
        .filter(
          (option, index, optionList) =>
            optionList.findIndex(
              (o) => o.type === option.type && o.label === option.label
            ) === index
        ),
      ...(selectReferent.groupList ?? []).map((group) => ({
        label: group.name,
        type: "group",
      })),
    ],
    [
      selectReferent.groupList,
      selectReferent.profileList,
      selectReferent.userList,
    ]
  );

  // 備考欄で使用する変数
  const RichTextField = ({ control, setValue, name, label }: any) => {
    const value = useWatch({
      control,
      name,
    });

    const editor = useEditor({
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: label,
        }),
        Image.configure({
          allowBase64: true,
        }),
      ],
      content: value || "",
      editorProps: {
        handleDOMEvents: {
          // 画像ファイルのドラッグ＆ドロップ対応
          drop: (view: EditorView, event: DragEvent) => {
            // ドロップされたファイルを取得
            const files = event.dataTransfer?.files;
            if (!files || files.length === 0) return false;

            // ファイルの種類チェックと Base64 変換
            Array.from(files).forEach((file) => {
              if (file.type.startsWith("image/")) {
                const reader = new FileReader();
                reader.onload = () => {
                  const base64 = reader.result as string;
                  editor
                    ?.chain()
                    .focus()
                    .setImage({ src: base64 })
                    .run();
                };
                reader.readAsDataURL(file);
              }
            });

            event.preventDefault();
            return true;
          },
        },
        attributes: {
          style: "min-height:300px; padding:12px; box-sizing:border-box; line-height:1.2;",
          class: 'prose-editor',
        },
      },
      onUpdate: ({ editor }: { editor: any }) => {
        setValue(name, editor.getHTML());
      },
    });

    return (
      <Box sx={{ width: '100%' }}>
        <FormControl fullWidth >
          <Box
            sx={{
              border: 1,
              borderRadius: 1,
              padding: 0.5,
              minHeight: 56,
              '&:focus-within': {
                borderColor: 'primary.main',
              },
              // プレースホルダー表示用 CSS
              '.prose-editor p.is-empty:first-child::before': {
                content: 'attr(data-placeholder)',
                color: '#9e9e9e',
                float: 'left',
                height: 0,
                pointerEvents: 'none',
              },
              '.prose-editor p.is-empty': {
                margin: 0, // プレースホルダーがずれないように
              },
            }}
          >
            <Controller
              name={name}
              control={control}
              render={() => (
                <EditorContent editor={editor} className="prose-editor" />
              )}
            />
          </Box>
        </FormControl>
      </Box>
    );
  };


  // ファイルを表示（直接ファイルを開く）
  const handleOpenFile = (file: File) => {
    const url = URL.createObjectURL(file); // ファイルの一時的なURLを生成
    // window.open(url); // ブラウザでファイルを開く（ファイル形式がブラウザ対応の場合）

    // ファイルを直接開こうとし、ブラウザで表示またはダウンロードを試みる
    const link = document.createElement("a");
    link.href = url;
    //  link.target = '_blank';  // ブラウザで新しいタブを開く
    link.download = file.name; // 元のファイル名でダウンロードさせる
    link.click();
  };

  const handleDelete = (file: File, index: number) => {
    const updatedFiles = getValues(`formEntryList.${index}.value`).filter(
      (f: File) => f !== file
    );
    setValue(`formEntryList.${index}.value`, updatedFiles);
  };

  const handleDownload = (file: File) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const renderFileList = (files: File[], index: number) => (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h6">アップされたファイル:</Typography>

      {/* ファイルアイコン + ファイル名 + 削除ボタンを横並びにまとめる */}
      <Box>
        <List>
          {files.map((file, fileIdx) => (
            <ListItem
              key={fileIdx}
              onMouseEnter={() => setHoveredFileIndex(fileIdx)}
              onMouseLeave={() => setHoveredFileIndex(null)}
            >
              <Description />
              <Typography
                onClick={() => handleOpenFile(file)}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  mx: 2,
                  "&:hover": {
                    cursor: "pointer",
                    textDecoration: "underline",
                  },
                }}
              >
                {file.name}
              </Typography>

              {/* 削除ボタン */}
              <Tooltip title="削除">
                <IconButton
                  onClick={() => handleDelete(file, index)}
                  color="error"
                  size="small"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  // 申請者情報欄を作成
  const applicantField = (applicantList: Applicant[]) => (
    <Grid sx={{ width: "311px" }}>
      <Controller
        {...register("applicant", {
          required: { value: true, message: "申請者を選択してください。" },
        })}
        render={({ field }) => (
          <Autocomplete
            options={applicantList}
            getOptionLabel={(option: Applicant) =>
              option.lastName + option.firstName
            }
            value={field.value || null}
            onChange={(event, newValue) => field.onChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="申請者"
                required
                error={!!errors.applicant}
                helperText={errors.applicant?.message}
                //入力不可(disabled)だが文字色は変更しないように
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87)", // 通常のテキスト色
                  },
                }}
              />
            )}
            multiple={false}
            disabled
            isOptionEqualToValue={(
              option: Applicant,
              value: Applicant | null
            ) => option.id === value?.id}
          />
        )}
      />
    </Grid>
  );

  // 承認者情報欄を作成
  const approverField = (selectApproverList: Approver[], index: number) => (
    <Grid sx={{ width: "311px" }}>
      <Controller
        {...register(`approvalActivityList.${index}.approverList`, {
          required: { value: true, message: "承認者を選択してください。" },
        })}
        render={() => (
          <Autocomplete
            options={selectApproverList}
            getOptionLabel={(option) =>
              option.approverLastName + option.approverFirstName
            }
            value={
              getValues(`approvalActivityList.${index}.approverList`) ?? []
            }
            onChange={(event, newValue) =>
              setValue(`approvalActivityList.${index}.approverList`, newValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="承認者"
                required
                error={!!errors.approvalActivityList?.[index]?.approverList}
                helperText={
                  errors.approvalActivityList?.[index]?.approverList?.message
                }
              />
            )}
            multiple={true}
            isOptionEqualToValue={(
              option: Approver,
              value: Approver
            ) => option.id === value.id}
          />
        )}
      />
    </Grid>
  );

  // 参照者情報欄を作成
  const selectedReferent = useWatch({
    control,
    name: "referent",
  });
  const referentField = (options: SelectReferentOption[]) => {
    const onChange = (newValue: SelectReferentOption[]) => {
      const referentUserList: ReferentUser[] = [];
      const referentProfileList: Profile[] = [];
      const referentGroupList: Group[] = [];
      newValue.forEach((value) => {
        switch (value.type) {
          case "user":
            (selectReferent.userList ?? [])
              .filter((user) => user.lastName + user.firstName === value.label)
              .forEach((v) => referentUserList.push(v));
            return;
          case "profile":
            (selectReferent.profileList ?? [])
              .filter((profile) => profile.value === value.label)
              .forEach((v) => referentProfileList.push(v));
            return;
          case "group":
            (selectReferent.groupList ?? [])
              .filter((group) => group.name === value.label)
              .forEach((v) => referentGroupList.push(v));
            return;
          default:
            return;
        }
      });
      console.log({
        userList: referentUserList.length > 0 ? referentUserList : undefined,
        profileList:
          referentProfileList.length > 0 ? referentProfileList : undefined,
        groupList: referentGroupList.length > 0 ? referentGroupList : undefined,
      });
      setValue("referent", {
        userList: referentUserList.length > 0 ? referentUserList : undefined,
        profileList:
          referentProfileList.length > 0 ? referentProfileList : undefined,
        groupList: referentGroupList.length > 0 ? referentGroupList : undefined,
      });
    };

    // 現在の参照者のデータを取得するために value に変換（表示復元のため）
    const selectedOptions: SelectReferentOption[] = [];

    (selectedReferent?.userList ?? []).forEach((u) =>
      selectedOptions.push({
        label: u.lastName + u.firstName,
        type: "user",
      })
    );
    (selectedReferent?.profileList ?? []).forEach((p) =>
      selectedOptions.push({
        label: p.value,
        type: "profile",
      })
    );
    (selectedReferent?.groupList ?? []).forEach((g) =>
      selectedOptions.push({
        label: g.name,
        type: "group",
      })
    );

    return (
      <Grid sx={{ width: "311px" }}>
        <Controller
          {...register("referent")}
          render={() => (
            <Autocomplete
              options={options}
              getOptionLabel={(option: SelectReferentOption) => option.label}
              onChange={(event, newValue) => onChange(newValue)}
              value={selectedOptions}
              renderInput={(params) => <TextField {...params} label="参照者" />}
              multiple={true}
            />
          )}
        />
      </Grid>
    );
  };

  const FileUploadField = ({
    index,
    label,
    isRequired,
    validation,
    onDrop,
    renderFileList,
  }: {
    index: number;
    label: string;
    isRequired?: boolean;
    validation: ValidationMessage;
    onDrop: (acceptedFiles: File[], index: number) => void;
    renderFileList: (files: File[], index: number) => React.JSX.Element;
  }) => {

    const { control } = useFormContext<ApplicationData>();
    const watchedFiles = useWatch({ name: `formEntryList.${index}.value`, control });
    const safeFiles = Array.isArray(watchedFiles) ? watchedFiles : [];

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      // 受け取った全ファイルを追加する（先頭1件ではなく）
      onDrop: (acceptedFiles) => onDrop(acceptedFiles, index),
    });

    return (
      <Grid size={12}>
        <FormControl
          fullWidth
          error={validation.isVisible}
        >
                
          {/* ラベル */}
          <FormLabel 
            sx={(theme) => ({
                ...theme.typography.h6,
                mb: 1,
                display: "inline-flex",
                alignItems: "center",
                color: validation.isVisible ? theme.palette.error.main : "inherit",
            })
          }>
            {label}
            {isRequired && (
              <Box 
                component="span" 
                sx={(theme) => ({
                  ml: 0.5,
                  color: validation.isVisible ? theme.palette.error.main : "inherit",
                })}
              >
                *
              </Box>
            )}
          </FormLabel>

          {/*必須チェックのエラーメッセージ*/}
          <Box sx={{ mt: 2 }}>
            {validation.isVisible && (
              <FormHelperText error>{validation.message}</FormHelperText>
            )}
          </Box>

          <Box
            {...getRootProps()}
            sx={{
              p: 2,
              mt: 1,
              border: "2px dashed #3f51b5",
              borderRadius: "8px",
              textAlign: "center",
              alignItems: "center",
              backgroundColor: isDragActive ? "#f0f4ff" : "#f5f5f5",
              cursor: "pointer",
              width: "100%",
            }}
          >
            <input {...getInputProps()} />
            <Typography variant="h6">
              {isDragActive
                ? "ファイルをドロップしてください"
                : "ここにファイルをドラッグ＆ドロップ、またはクリックしてファイルを選択"}
            </Typography>
            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 2 }}
            >
              ファイルを選択
            </Button>
          </Box>
        </FormControl>

        {/* ファイルリストの表示 */}
        {safeFiles.length > 0 && renderFileList(safeFiles, index)}
      </Grid>
    );
  };


  const AMOUNT_REGEX = /^(?:0|[1-9][0-9]*)$/;

  const validateAmount = (value: unknown, label: string) => {
    const v = value == null ? "" : String(value);

    // 未入力は required が担当（必須でなければ OK）
    if (v === "") return true;

    // 空白のみはNG（""はOK）
    if (v.trim() === "") return `${label}は0以上の整数で入力してください（カンマ不可）`;

    // 0以上の整数のみ（カンマ/マイナス/小数NG）
    if (!AMOUNT_REGEX.test(v)) {
      return `${label}は0以上の整数で入力してください（カンマ不可）`;
    }
    return true;
  };


  const renderField = (formDataRender: FormDataRender) => {
    const index = formDataRender.index;

    const isValueRequired =
      formDataRender.type !== "switch" &&
      formDataRender.type !== "linkList" &&
      !!formDataRender.isRequired;


    const formDataRegister = register(
      `formEntryList.${index}.value`,
      {
        required: {
          value:
            isValueRequired,
          message:
            formDataRender.type !== "switch"
              ? formDataRender.name +
              "を" +
              (["autocomplete", "select", "fileUpload"].includes(
                formDataRender.type ?? ""
              )
                ? "選択"
                : "入力") +
              "してください。"
              : "",

        },

        validate: (v) => {
          switch (formDataRender.type) {
            case "amount":
              return validateAmount(v, formDataRender.name);

            case "switch":
              return (
                !formDataRender.isRequired ||
                v === true ||
                `${formDataRender.name}をONにしてください。`
              );

            default:
              return true;
          }
        },
      }
    );

    const validation: ValidationMessage = {
      isVisible: !!errors.formEntryList?.[formDataRender.index]?.value,
      message:
        errors.formEntryList?.[
          formDataRender.index
        ]?.value?.message?.toString(),
    };


    const formatWithComma = (value: string | number) => {
      if (value === "" || value == null) return "";
      const num = Number(value);
      if (isNaN(num)) return "";
      return num.toLocaleString("ja-JP");
    };

    const removeComma = (value: string) => value.replace(/,/g, "");


    switch (formDataRender.type) {
      case "text":
        return (
          <Grid size={12}>
            <TextField
              label={formDataRender.name}
              {...formDataRegister}
              defaultValue={formDataRender.value}
              required={formDataRender.isRequired}
              error={validation.isVisible}
              helperText={validation.message}
              fullWidth
            />
          </Grid>
        );
      case "textLong":
        return (
          <Grid size={12}>
            <TextField
              label={formDataRender.name}
              {...formDataRegister}
              defaultValue={formDataRender.value}
              required={formDataRender.isRequired}
              error={validation.isVisible}
              helperText={validation.message}
              multiline={true}
              rows={4}
              fullWidth
            />
          </Grid>
        );
      case "autocomplete":
        return (
          <Grid size={12}>
            <Controller
              {...formDataRegister}
              render={() => (
                <Autocomplete
                  options={[]}
                  getOptionLabel={(option: any) => option.name}
                  defaultValue={formDataRender.value}
                  onChange={(newValue) =>
                    setValue(
                      `formEntryList.${formDataRender.index}.value`,
                      newValue
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={formDataRender.name}
                      required={formDataRender.isRequired}
                      error={validation.isVisible}
                      helperText={validation.message}
                    />
                  )}
                  multiple={false}
                />
              )}
            />
          </Grid>
        );
      case "select":
        return (
          <Grid sx={{ width: "311px" }}>
            <FormControl fullWidth>
              <TextField
                select
                label={formDataRender.name}
                {...formDataRegister}
                defaultValue={formDataRender.value}
                required={formDataRender.isRequired}
                error={validation.isVisible}
                helperText={validation.message}
              >
                {formDataRender.selectItemList
                  ?.sort((a, b) => a.order - b.order)
                  .map((select: any) => (
                    <MenuItem key={select.id} value={select.value}>
                      {select.value}
                    </MenuItem>
                  ))}
              </TextField>
            </FormControl>
          </Grid>
        );
      case "date":
        return (
          <Controller
            {...formDataRegister}
            render={() => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={formDataRender.name}
                  format="YYYY/MM/DD"
                  defaultValue={dayjs(formDataRender.value)}
                  onChange={(newValue) =>
                    setValue(
                      `formEntryList.${formDataRender.index}.value`,
                      newValue?.format("YYYY/MM/DD")
                    )
                  }
                  slotProps={{
                    textField: {
                      required: formDataRender.isRequired,
                      error: validation.isVisible,
                      helperText: validation.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        );
      case "switch":
        return (
          <Grid size={12}>
            <FormControl fullWidth error={validation.isVisible}>
              <Controller
                {...formDataRegister}
                render={() => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          !!getValues(`formEntryList.${formDataRender.index}.value`)
                        }
                        onChange={(e) =>
                          setValue(
                            `formEntryList.${formDataRender.index}.value`,
                            e.target.checked,
                            { shouldValidate: true }
                          )
                        }
                        defaultChecked={!!formDataRender.value}
                      />
                    }
                    label={formDataRender.name}
                    required={formDataRender.isRequired}
                    
                    sx={(theme) => ({
                        "& .MuiFormControlLabel-label": {
                            color: validation.isVisible ? theme.palette.error.main : "inherit",
                        },
                        "& .MuiFormControlLabel-asterisk": {
                          color: validation.isVisible ? theme.palette.error.main : "inherit",
                        },
                    })}

                  />
                )}
              />
          
              {validation.isVisible && (
                <FormHelperText error>{validation.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

        );
      case "fileUpload":
        return (
          <FileUploadField
            label={formDataRender.name}
            index={formDataRender.index}
            isRequired={formDataRender.isRequired}
            validation={validation}
            onDrop={onDrop}
            renderFileList={renderFileList}
          />
        );
      case "linkList":
        return (
          <Grid size={12}>
            <Controller
              name={`formEntryList.${formDataRender.index}.formLinks`}
              control={control}
              defaultValue={[]}
              rules={{
                validate: (links) => {
                  if (!formDataRender.isRequired) return true;
                  const list = Array.isArray(links) ? links : [];
                  const hasValid = list.some(l => (l?.url ?? "").trim().length > 0);
                  return hasValid || `${formDataRender.name}を入力してください。`;
                },
              }}
              render={({ field, fieldState }) => (
                <>
                  <FormLinkList
                    label={formDataRender.name}
                    isRequired={formDataRender.isRequired}
                    value={field.value}
                    onChange={(newLinks) => field.onChange(newLinks)}
                    errorMessage={fieldState.error?.message}
                    hasError={!!fieldState.error}
                  />
                </>
              )}
            />
          </Grid>
        );
      case "applicationNumber":
        return (
          <Grid size={12}>
            <Controller
              name={`formEntryList.${formDataRender.index}.value`}
              control={control}
              render={({ field }) => (
                <ApplicationNumberFields
                  value={field.value}
                  onChange={field.onChange}
                  required={!!formDataRender.isRequired}
                  error={validation.isVisible}
                  helperText={validation.message}
                />
              )}
            />
          </Grid>
        );
      case "richText":
        return (
          <RichTextField
            control={control}
            setValue={setValue}
            name={`formEntryList.${formDataRender.index}.value`}
            label={formDataRender.name}
          />
        );

      case "amount":
        return (
          <Grid size={12}>
            <Controller
              name={`formEntryList.${formDataRender.index}.value`}
              control={control}
              rules={{
                required: formDataRender.isRequired,
                validate: (v) => validateAmount(v, formDataRender.name),
              }}
              render={({ field }) => (
                <TextField
                  label={formDataRender.name}
                  value={formatWithComma(field.value)}
                  onChange={(e) => {
                    const raw = removeComma(e.target.value);

                    // 数字以外は無視
                    if (!/^\d*$/.test(raw)) return;

                    field.onChange(raw);
                  }}
                  required={formDataRender.isRequired}
                  error={validation.isVisible}
                  helperText={validation.message}
                  fullWidth
                  inputProps={{
                    inputMode: "numeric",
                  }}
                  placeholder="例: 12,000"
                />
              )}
            />
          </Grid>
        );
    }
  };

  return (
    <Box
      sx={{
        backgroundImage: "url('silkcloth.jpg')",
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          // タグ名を取得
          const tagName = (e.target as HTMLElement).tagName.toLowerCase();
          if (tagName !== "textarea") {
            e.preventDefault(); // textareaではない場合はEnterで提出禁止
          }
          // textarea中でEnterで改行できる
        }
      }}
    >
      <Paper
        sx={{ m: 1, mx: "auto", justifyItem: "center", maxWidth: "900px" }}
      >
        <Grid container rowSpacing={3} sx={{ p: 4 }}>
          <Grid size={12}>
            <Typography variant="h2">
              {useWatch({ control, name: "type" })}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h3">
              {useWatch({ control, name: "name" })}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h3" sx={{ fontWeight: "normal" }}>
              {useWatch({ control, name: "description" })}
            </Typography>
          </Grid>

          {/** 申請者 */}
          <>{applicantField(applicantList)}</>

          {/** 申請項目 */}
          {formDataField.fields
            .sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0))
            .reduce(
              (
                sectionList: FormDataSection[],
                formData: FormData,
                index: number
              ) => {
                !formDataField.fields.some(
                  (formData: FormData) => formData.type === "section"
                ) &&
                  sectionList.push({
                    sectionName: undefined,
                    formDataList: [],
                  });
                formData.type === "section"
                  ? sectionList.push({
                    sectionName: formData.name,
                    formDataList: [],
                  })
                  : sectionList[sectionList.length - 1].formDataList?.push({
                    ...formData,
                    index: index,
                  });
                return sectionList;
              },
              [] as FormDataSection[]
            )
            .map((section: FormDataSection) => (
              <Grid size={12} sx={{ my: 1 }}>
                {/* <Box sx={{ p: 1, border: "1px solid #BBBBBB" }}> */}
                <Grid container rowSpacing={3}>
                  {section.sectionName && (
                    <Grid size={12}>
                      <Typography variant="h6">
                        {section.sectionName}
                      </Typography>
                    </Grid>
                  )}
                  {section.formDataList?.map(
                    (formDataRender: FormDataRender) => (
                      <>{renderField(formDataRender)}</>
                    )
                  )}
                </Grid>
              </Grid>
            ))}

          {/** 承認アクティビティ */}
          {approverActivityField.fields
            .sort((a, b) => a.order - b.order)
            .map((approvalActivity: ApprovalActivity, index) => (
              <Grid size={12} sx={{ my: 1 }}>
                {/* <Box sx={{ p: 1, border: "1px solid #BBBBBB" }}> */}
                <Grid container rowSpacing={3}>
                  <Grid size={12}>
                    <Typography variant="h6">
                      {approvalActivity.activityName}
                    </Typography>
                  </Grid>
                  <>
                    {approverField(
                      approvalActivity.selectApproverList ?? [],
                      index
                    )}
                  </>
                </Grid>
                {/* </Box> */}
              </Grid>
            ))}

          {/** 参照者 */}
          <>{referentField(selectReferentOptions)}</>

          <Grid
            size={12}
            justifyContent={"flex-end"}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              marginTop: 2,
              marginBottom: 2,
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={() => setShowCancelDialog(true)}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={onTempSave}
            >
              一時保存
            </Button>
            <Button
              type="submit"
              variant="contained"
            >
              申請
            </Button>
          </Grid>
          {/* ) : (
        <Typography variant="h6">Loading...</Typography>
      )} */}
        </Grid>
      </Paper>

      {/* キャンセル確認ダイアログ */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>キャンセル確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            入力内容は保存されていません。入力を破棄しますか?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>戻る</Button>
          <Button
            onClick={() => {
              navigate("/SearchWorkflow");
              setShowCancelDialog(false);
            }}
            color="error"
          >
            破棄
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
